import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@fontsource/cormorant-garamond/latin-600.css";
import "@fontsource/cormorant-garamond/latin-700.css";
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-600.css";
import "@fortawesome/fontawesome-free/css/fontawesome.min.css";
import "@fortawesome/fontawesome-free/css/solid.min.css";
import { CardView } from "./components/CardView";
import { AccessibleDialog } from "./components/AccessibleDialog";
import { chooseAiAction, coachRecommendation, explainAction, type AiExplanation, type CoachAdvice } from "./engine/ai";
import { createAiBridge } from "./engine/ai-bridge";
import { effectiveSuit, followSuitInfo, isLeftBower, isRightBower, legalCards, trickWinner } from "./engine/cards";
import { refreshDailyRelease, dailyCallDecision, dailyCallMatched, londonDateKey, scenarioForDate, type DailyCallDecision } from "./engine/daily";
import { isHumanTurn, matchMetrics, newGame, publicView, reduceGame } from "./engine/game";
import { swingMoments, type SwingKind } from "./engine/review";
import {
  clearAllLocalData,
  claimScoreEvent,
  clearGame,
  DEFAULT_SETTINGS,
  loadGame,
  loadSettings,
  loadStats,
  loadVisited,
  markVisited,
  pendingDaily,
  recordHandStats,
  saveGame,
  saveSettings,
  saveStats,
} from "./engine/persistence";
import type { Difficulty, GameAction, GameSettings, GameState, LocalStats, Mode, Seat, Suit } from "./engine/types";
import { localeFromPath, bowerSuffix, fill, localizeExplanation, seatText, suitSymbol, suitText, t, type Locale } from "./i18n";
import "./styles.css";

const HUMAN: Seat = 0;
const locale = localeFromPath();
const rulesPath = locale === "zh" ? "/zh/rules/" : locale === "ja" ? "/ja/rules/" : "/rules/";
// The tutorial is a normal quick-rules match on a pinned seed; only the UI
// overlays a step machine. No engine rules change.
const TUTORIAL_SEED = "tutorial-v1";

let sharedAudioContext: AudioContext | null = null;

function audioContext(): AudioContext | null {
  try {
    if (!sharedAudioContext) sharedAudioContext = new window.AudioContext();
    if (document.hidden) {
      void sharedAudioContext.suspend().catch(() => undefined);
      return null;
    }
    if (sharedAudioContext.state === "suspended") void sharedAudioContext.resume().catch(() => undefined);
    return sharedAudioContext;
  } catch {
    return null;
  }
}

function soundEffect(kind: "card" | "trick" | "score", enabled: boolean) {
  if (!enabled) return;
  try {
    const context = audioContext();
    if (!context) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = kind === "score" ? 560 : kind === "trick" ? 300 : 180;
    gain.gain.setValueAtTime(0.035, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.12);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.12);
  } catch {
    // Sound is non-critical and browser gesture policies may decline it.
  }
}

export default function App() {
  const [settings, setSettings] = useState<GameSettings>(() => ({ ...DEFAULT_SETTINGS, ...loadSettings(), locale }));
  const [saved, setSaved] = useState(() => loadGame());
  const [state, setState] = useState<GameState>(() => {
    if (saved) return saved;
    const initial = newGame("quick", locale);
    return settings.coach ? { ...initial, assisted: true } : initial;
  });
  const [stats, setStats] = useState<LocalStats>(() => loadStats());
  // First-visit entry layer: only when there is no saved match and the
  // versioned visited marker is not set. Returning players never see it.
  const [showEntry, setShowEntry] = useState(() => saved === null && !loadVisited());
  // Tutorial step machine state; `ack` counts dismissed reading steps.
  const [tutorial, setTutorial] = useState<{ ack: number } | null>(null);
  const [modal, setModal] = useState<"rules" | "settings" | "log" | null>(null);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [hintOpen, setHintOpen] = useState(settings.coach);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [aloneIntent, setAloneIntent] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [aiNote, setAiNote] = useState<{ seat: Seat; explanation: AiExplanation } | null>(null);
  const [bridge] = useState(() => createAiBridge());
  const actionLock = useRef(false);
  const lastScoredNonce = useRef(-1);

  useEffect(() => () => bridge.destroy(), [bridge]);

  // The Daily unlock follows the Europe/London date key only. It recomputes
  // on startup, window focus, pageshow and visibility restore, and on a
  // periodic boundary check while the page stays open; an in-progress Daily
  // keeps its original date key.
  const [londonToday, setLondonToday] = useState(() => londonDateKey());
  useEffect(() => {
    const refresh = () => {
      void refreshDailyRelease().then(() => { const recovered = pendingDaily(); if (recovered) setSaved(recovered); setLondonToday(londonDateKey()); });
      setLondonToday((current) => {
        const next = londonDateKey();
        return next === current ? current : next;
      });
    };
    refresh();
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : locale;
    document.documentElement.classList.toggle("reduced-motion", settings.reducedMotion);
    saveSettings(settings);
  }, [settings]);

  useEffect(() => {
    // While the first-visit entry layer is up, nothing has been chosen yet —
    // do not create a save, so a refresh still lands on the entry layer.
    if (!showEntry) saveGame(state);
  }, [state, showEntry]);

  useEffect(() => {
    if (!state.lastHand || lastScoredNonce.current === state.actionNonce) return;
    lastScoredNonce.current = state.actionNonce;
    if (!claimScoreEvent(state.gameId, state.actionNonce)) return;
    // The scored-hand transition is the external local-storage event being synchronized here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStats((current) => {
      const next = recordHandStats(current, state);
      saveStats(next);
      return next;
    });
    soundEffect("score", settings.sound);
  }, [settings.sound, state]);

  const dispatch = useCallback(
    (action: GameAction) => {
      if (actionLock.current) return;
      actionLock.current = true;
      try {
        setState((current) => reduceGame(current, action));
        setNotice("");
        setSelectedCard(null);
        if ("seat" in action) setAiNote(null);
        soundEffect(action.type === "PLAY_CARD" ? "card" : "trick", settings.sound);
      } catch (error) {
        void error;
        setNotice(t(locale, "actionUnavailable"));
      } finally {
        window.setTimeout(() => {
          actionLock.current = false;
        }, 80);
      }
    },
    [settings.sound],
  );

  useEffect(() => {
    if (state.phase === "trickResult") {
      const delay = settings.reducedMotion ? 100 : 720;
      const timer = window.setTimeout(() => dispatch({ type: "COLLECT_TRICK" }), delay);
      return () => window.clearTimeout(timer);
    }
    if (isHumanTurn(state) || ["handResult", "completed"].includes(state.phase)) return;
    if (!["calling1", "calling2", "discard", "playing"].includes(state.phase)) return;
    const nonce = state.actionNonce;
    const seat = state.currentSeat;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      // Decision runs in the AI worker; the main thread only dispatches the
      // result, and only if the position is still current (nonce guard).
      bridge.request(publicView(state, seat), settings.difficulty).then(({ action, explanation }) => {
        if (cancelled) return;
        setState((current) => {
          if (current.actionNonce !== nonce || current.currentSeat === HUMAN) return current;
          return reduceGame(current, action);
        });
        setAiNote({ seat, explanation });
      }).catch(() => undefined);
    }, settings.reducedMotion ? 80 : 460);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      bridge.cancel();
    };
  }, [bridge, dispatch, settings.difficulty, settings.reducedMotion, state]);

  const humanView = useMemo(() => publicView(state, HUMAN), [state]);
  const legalIds = useMemo(
    () =>
      state.phase === "playing" && state.trump
        ? legalCards(state.hands[HUMAN], state.currentTrick, state.trump).map((card) => card.id)
        : state.hands[HUMAN].map((card) => card.id),
    [state],
  );
  const coach = useMemo(() => coachRecommendation(humanView), [humanView]);
  // Coach text is rendered from the same structured facts the worker returns,
  // computed on the very same PublicView, so hint and decision can never drift.
  const coachExplanation = useMemo(() => {
    if (!isHumanTurn(state)) return null;
    try {
      return explainAction(humanView, chooseAiAction(humanView, "standard"), "standard");
    } catch {
      return null;
    }
  }, [humanView, state]);
  const coachCardId = "cardId" in coach ? coach.cardId : undefined;
  const resultMetrics = useMemo(() => matchMetrics(state), [state]);
  const resultCallRate = resultMetrics.calls ? Math.round(resultMetrics.callsMade / resultMetrics.calls * 100) : 0;
  const todayDaily = stats.dailyResults.find((result) => result.calendar === "london" && result.date === londonToday);
  const dailyScenario = useMemo(
    () => (state.mode === "daily" && state.dailyDate ? scenarioForDate(state.dailyDate) : null),
    [state.mode, state.dailyDate],
  );
  const dailyDecision = useMemo(
    () => (dailyScenario && state.phase === "completed" ? dailyCallDecision(state) : null),
    [dailyScenario, state],
  );
  const swings = useMemo(
    () => (state.phase === "completed" && state.mode !== "daily" ? swingMoments(state).slice(0, 3) : []),
    [state],
  );

  // Tutorial step derived from the live game: reading steps 0–1 wait for
  // acknowledgment, the rest follow phase/history. Step 6 is a quiet
  // "finish the hand" mode. Not persisted: a mid-tutorial refresh simply
  // resumes the saved hand as a normal match.
  const tutorialStep = useMemo(() => {
    if (!tutorial || state.mode !== "quick" || state.seed !== TUTORIAL_SEED || state.handNumber !== 0) return null;
    if (state.phase === "handResult" || state.phase === "completed") return 5;
    // Reading steps only gate the calling lesson; if the player calls early,
    // the machine catches up to the live game instead of blocking. Note maker
    // is a seat index — 0 (the human) is falsy, so compare against null.
    if (state.maker === null && tutorial.ack < 2) return tutorial.ack;
    if (state.maker === null) return 2;
    if (state.tricks.length === 0) return 3;
    if (tutorial.ack < 3) return 4;
    return 6;
  }, [tutorial, state]);
  const tutorialActive = tutorialStep !== null;
  const humanCallEvent = useMemo(
    () => state.history.find((event) => /^0:(order|call|pass)/.test(event)),
    [state.history],
  );

  const beginMatch = (mode: Mode) => {
    let initial: GameState;
    try {
      initial = newGame(mode, locale);
    } catch {
      // Fail closed: a London date outside the preloaded library means the
      // Daily is unavailable; quick/coach play is never affected.
      setNotice(t(locale, "dailyUnavailable"));
      return;
    }
    const next = mode === "coach" || settings.coach ? { ...initial, assisted: true } : initial;
    setState(next);
    setHintOpen(mode === "coach" || settings.coach);
    setTutorial(null);
    clearGame();
    setSaved(null);
  };

  const beginTutorial = () => {
    const initial = newGame("quick", locale, new Date(), TUTORIAL_SEED);
    // The tutorial itself is guidance, so the hand is marked assisted.
    setState({ ...initial, assisted: true });
    setTutorial({ ack: 0 });
    clearGame();
    setSaved(null);
  };

  const chooseEntry = (kind: "tutorial" | "quick" | "daily") => {
    markVisited();
    setShowEntry(false);
    if (kind === "tutorial") beginTutorial();
    else beginMatch(kind);
  };

  const start = (mode: Mode) => {
    if (state.history.length > 1 && !["completed", "handResult"].includes(state.phase)) {
      setPendingMode(mode);
      return;
    }
    beginMatch(mode);
  };

  const playOrExplain = (cardId: string) => {
    if (state.phase === "discard" && state.currentSeat === HUMAN) {
      setFocusedIndex((current) => Math.min(current, Math.max(0, state.hands[HUMAN].length - 2)));
      dispatch({ type: "DISCARD", seat: HUMAN, cardId });
      return;
    }
    if (state.phase !== "playing" || state.currentSeat !== HUMAN) return;
    if (!legalIds.includes(cardId)) {
      const info = state.trump ? followSuitInfo(state.hands[HUMAN], state.currentTrick, state.trump) : null;
      setNotice(
        info && info.held > 0
          ? fill(t(locale, "legalReasonFollow"), { suit: suitText[locale][info.leadSuit], count: String(info.held) })
          : t(locale, "legalReason"),
      );
      return;
    }
    if (settings.confirmPlay && selectedCard !== cardId) {
      setSelectedCard(cardId);
      return;
    }
    setFocusedIndex((current) => Math.min(current, Math.max(0, state.hands[HUMAN].length - 2)));
    dispatch({ type: "PLAY_CARD", seat: HUMAN, cardId });
  };

  const onCardKey = (event: React.KeyboardEvent<HTMLButtonElement>, index: number, cardId: string) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const delta = event.key === "ArrowRight" ? 1 : -1;
      const next = (index + delta + state.hands[HUMAN].length) % state.hands[HUMAN].length;
      setFocusedIndex(next);
      document.querySelectorAll<HTMLButtonElement>("[data-card-id]")[next]?.focus();
    } else if (event.key === " ") {
      event.preventDefault();
      setSelectedCard(cardId);
    } else if (event.key === "Enter") {
      event.preventDefault();
      playOrExplain(cardId);
    }
  };

  const updateSetting = <K extends keyof GameSettings>(key: K, value: GameSettings[K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const shareResult = async () => {
    const modeLabel = state.dailyDate ?? t(locale, state.mode === "daily" ? "daily" : state.mode === "coach" ? "coach" : "quick");
    const resultMetrics = matchMetrics(state);
    const callRate = resultMetrics.calls ? Math.round(resultMetrics.callsMade / resultMetrics.calls * 100) : 0;
    const text = [
      "Trump Table",
      modeLabel,
      `${state.scores[0]}–${state.scores[1]}`,
      `${t(locale, "hands")} ${resultMetrics.hands}`,
      `${t(locale, "callSuccess")} ${callRate}%`,
      `${t(locale, "euchresLabel")} ${resultMetrics.euchres}`,
      t(locale, state.assisted ? "assisted" : "unassisted"),
      `${"●".repeat(state.trickCounts[0])}${"○".repeat(state.trickCounts[1])}`,
    ].join(" · ");
    try {
      if (navigator.share) await navigator.share({ text, url: "https://trumptable.legen.uk/" });
      else await navigator.clipboard.writeText(text);
      setNotice(text);
    } catch {
      setNotice(text);
    }
  };

  const humanCanCall = state.currentSeat === HUMAN && (state.phase === "calling1" || state.phase === "calling2");
  const trickWinnerSeat =
    state.phase === "trickResult" && state.trump ? trickWinner(state.currentTrick, state.trump).seat : null;

  return (
    <div className="app-shell">
      <header className="topbar" inert={showEntry}>
        <div className="brand" aria-label="Trump Table">
          <div className="brand-title"><i className="fa-solid fa-crown" aria-hidden="true" /> TRUMP TABLE</div>
          <div className="brand-sub">{t(locale, "brandSub")}</div>
        </div>
        <nav className="mode-nav" aria-label={t(locale, "gameModes")}>
          <button onClick={() => start("quick")} className={state.mode === "quick" ? "active" : ""}>
            <i className="fa-solid fa-bolt" aria-hidden="true" /> {t(locale, "quick")}
          </button>
          <button onClick={() => start("daily")} className={state.mode === "daily" ? "active" : ""}>
            <i className="fa-solid fa-calendar-day" aria-hidden="true" /> {t(locale, "daily")}
          </button>
          <button onClick={() => {
            const enabled = !settings.coach;
            updateSetting("coach", enabled);
            setHintOpen(enabled);
            if (enabled) setState((current) => ({ ...current, assisted: true }));
          }}>
            <i className="fa-solid fa-brain" aria-hidden="true" /> {t(locale, "coach")} <span>{settings.coach ? t(locale, "on") : t(locale, "off")}</span>
          </button>
        </nav>
        <Scoreboard state={state} />
        <nav className="utility-nav" aria-label={t(locale, "tableUtilities")}>
          <button onClick={() => setModal("rules")}><i className="fa-solid fa-book-open" aria-hidden="true" /> {t(locale, "rules")}</button>
          <button onClick={() => updateSetting("sound", !settings.sound)}><i className={`fa-solid ${settings.sound ? "fa-volume-high" : "fa-volume-xmark"}`} aria-hidden="true" /> {t(locale, "sound")}</button>
          <button onClick={() => setModal("settings")} aria-label={t(locale, "settings")}><i className="fa-solid fa-gear" aria-hidden="true" /></button>
          <div className="locale-switcher" aria-label={t(locale, "language")}>
            <a href="/" aria-current={locale === "en" ? "page" : undefined}>EN</a>
            <a href="/zh/" aria-current={locale === "zh" ? "page" : undefined}>中文</a>
            <a href="/ja/" aria-current={locale === "ja" ? "page" : undefined}>日本語</a>
          </div>
        </nav>
      </header>

      <main inert={showEntry}>
        {saved && saved.gameId !== state.gameId && (
          <button className="resume-banner" onClick={() => setState(saved)}>
            <i className="fa-solid fa-rotate-left" aria-hidden="true" /> {t(locale, "resume")}
          </button>
        )}
        <section className="game-stage" aria-label={t(locale, "playableTable")}>
          <SeatPanel seat={2} state={state} locale={locale} className="seat-north" />
          <SeatPanel seat={1} state={state} locale={locale} className="seat-west" />
          <SeatPanel seat={3} state={state} locale={locale} className="seat-east" />

          <div className="table-center">
            <div className="upcard-zone">
              <span>{state.trump ? t(locale, "trump") : t(locale, "candidate")}</span>
              <CardView card={state.upcard} trump={state.trump ?? state.upcard.suit} locale={locale} compact />
              <strong className={state.upcard.suit === "hearts" || state.upcard.suit === "diamonds" ? "red-suit" : ""}>
                {suitSymbol[state.trump ?? state.upcard.suit]} {suitText[locale][state.trump ?? state.upcard.suit]}
              </strong>
            </div>
            <div className="trick-zone" aria-label={t(locale, "currentTrick")}>
              <span className="trick-label">{t(locale, "currentTrick")}</span>
              {state.currentTrick.map((entry) => (
                <div key={entry.card.id} className={`trick-card trick-seat-${entry.seat} ${trickWinnerSeat === entry.seat ? "winning" : ""}`}>
                  <CardView card={entry.card} trump={state.trump} locale={locale} compact />
                </div>
              ))}
            </div>
          </div>

          <div className="call-area" aria-live="polite">
            <h2>{state.phase === "calling2" ? t(locale, "callRound2") : state.phase.startsWith("calling") ? t(locale, "callRound1") : phaseMessage(state, locale)}</h2>
            <p>{humanCanCall ? t(locale, state.phase === "calling2" && state.currentSeat === state.dealer ? "mustChoose" : "yourCall") : phaseDetail(state, locale)}</p>
            {aiNote && <p className="ai-note">{seatText[locale][aiNote.seat]} · {localizeExplanation(aiNote.explanation, locale, "event")}</p>}
            {humanCanCall && state.phase === "calling1" && (
              <p className="call-facts">{fill(t(locale, "callFacts"), {
                suit: suitText[locale][state.upcard.suit],
                trumpCount: String(trumpFacts(state.hands[HUMAN], state.upcard.suit).count),
                bowers: bowerSuffix(trumpFacts(state.hands[HUMAN], state.upcard.suit), locale),
              })}</p>
            )}
            {humanCanCall && state.phase === "calling1" && (
              <div className="action-row">
                <button onClick={() => dispatch({ type: "PASS", seat: HUMAN })}>{t(locale, "pass")}</button>
                <button className="primary" onClick={() => dispatch({ type: "ORDER_UP", seat: HUMAN })}>
                  {t(locale, "orderUp")} {suitSymbol[state.upcard.suit]}
                </button>
                <button onClick={() => dispatch({ type: "ORDER_UP", seat: HUMAN, alone: true })}>{t(locale, "alone")}</button>
              </div>
            )}
            {humanCanCall && state.phase === "calling2" && (
              <div className="suit-actions">
                {(["clubs", "diamonds", "hearts", "spades"] as Suit[]).filter((suit) => suit !== state.upcard.suit).map((suit) => (
                  <button key={suit} onClick={() => dispatch({ type: "CALL_SUIT", seat: HUMAN, suit, alone: aloneIntent })} className={suit === "hearts" || suit === "diamonds" ? "red-suit" : ""}>
                    {suitSymbol[suit]} <span>{suitText[locale][suit]}</span>
                    <span className="suit-fact">{fill(t(locale, "suitFactShort"), {
                      trumpCount: String(trumpFacts(state.hands[HUMAN], suit).count),
                      bowers: bowerSuffix(trumpFacts(state.hands[HUMAN], suit), locale),
                    })}</span>
                  </button>
                ))}
                <button className={aloneIntent ? "primary" : ""} aria-pressed={aloneIntent} onClick={() => setAloneIntent((current) => !current)}>{t(locale, "alone")}</button>
                {state.currentSeat !== state.dealer && <button onClick={() => dispatch({ type: "PASS", seat: HUMAN })}>{t(locale, "pass")}</button>}
              </div>
            )}
            {state.phase === "handResult" && state.lastHand && (
              <button className="primary" onClick={() => dispatch({ type: "NEXT_HAND" })}>{t(locale, "nextHand")}</button>
            )}
          </div>

          {tutorialActive && (
            <TutorialPanel
              step={tutorialStep}
              state={state}
              humanCallEvent={humanCallEvent}
              locale={locale}
              onNext={() => setTutorial((current) => (current ? { ack: current.ack + 1 } : current))}
              onExit={() => setTutorial(null)}
              onStartMatch={() => {
                setTutorial(null);
                beginMatch("quick");
              }}
            />
          )}

          {(settings.coach || state.mode === "coach") && !tutorialActive && (
            <aside className="coach-panel">
              <h3><i className="fa-solid fa-circle-info" aria-hidden="true" /> {t(locale, "coachInsight")}</h3>
              {hintOpen ? <p>{coachExplanation ? localizeExplanation(coachExplanation, locale, "recommendation") : localizeCoach(coach, locale)}</p> : null}
              <button onClick={() => { setHintOpen(!hintOpen); if (!hintOpen) setState((current) => ({ ...current, assisted: true })); }}>
                {t(locale, hintOpen ? "hideHint" : "showHint")}
              </button>
            </aside>
          )}

          <aside className="status-panel">
            <InfoRow icon="fa-circle-d" label={t(locale, "dealer")} value={seatText[locale][state.dealer]} />
            <InfoRow icon="fa-user-tie" label={t(locale, "maker")} value={state.maker === null ? t(locale, "noneYet") : seatText[locale][state.maker]} />
            <InfoRow icon="fa-clover" label={t(locale, "trump")} value={state.trump ? `${suitSymbol[state.trump]} ${suitText[locale][state.trump]}` : t(locale, "candidate")} />
            <InfoRow icon="fa-layer-group" label={t(locale, "tricks")} value={`${state.trickCounts[0]} – ${state.trickCounts[1]}`} />
          </aside>

          <div className="hand" aria-label={t(locale, "yourHand")}>
            {state.hands[HUMAN].map((card, index) => {
              const canAct = state.currentSeat === HUMAN && (state.phase === "playing" || state.phase === "discard");
              const playable = state.phase === "discard" || legalIds.includes(card.id);
              return (
                <CardView
                  key={card.id}
                  card={card}
                  trump={state.trump}
                  locale={locale}
                  playable={canAct && playable}
                  selected={selectedCard === card.id || coachCardId === card.id && hintOpen}
                  onClick={() => playOrExplain(card.id)}
                  onKeyDown={(event) => onCardKey(event, index, card.id)}
                  tabIndex={index === focusedIndex ? 0 : -1}
                />
              );
            })}
          </div>
          {notice && <div className="notice" role="status">{notice}</div>}
          <button className="log-button" onClick={() => setModal("log")}><i className="fa-solid fa-list" aria-hidden="true" /> {t(locale, "gameLog")}</button>
        </section>

        <section className="content-grid" aria-labelledby="rules-content">
          <header>
            <p>{t(locale, "heroEyebrow")}</p>
            <h1 id="rules-content">{t(locale, "howTitle")}</h1>
            <p>{t(locale, "howBody")}</p>
          </header>
          <article><i className="fa-solid fa-diamond" aria-hidden="true" /><h2>{t(locale, "bowerTitle")}</h2><p>{t(locale, "bowerBody")}</p></article>
          <article><i className="fa-solid fa-comments" aria-hidden="true" /><h2>{t(locale, "callingTitle")}</h2><p>{t(locale, "callingBody")}</p></article>
          <article><i className="fa-solid fa-trophy" aria-hidden="true" /><h2>{t(locale, "scoreTitle")}</h2><p>{t(locale, "scoreBody")}</p></article>
          <article><i className="fa-solid fa-people-arrows-left-right" aria-hidden="true" /><h2>{t(locale, "strategyTitle")}</h2><p>{t(locale, "strategyBody")}</p></article>
          <p className="rules-more"><a href={rulesPath}>{t(locale, "fullRules")} →</a></p>
        </section>

        <section className="records">
          <h2>{t(locale, "stats")}</h2>
          <dl>
            <div><dt>{t(locale, "wins")}</dt><dd>{stats.wins}</dd></div>
            <div><dt>{t(locale, "games")}</dt><dd>{stats.games}</dd></div>
            <div><dt>{t(locale, "winRate")}</dt><dd>{stats.games ? Math.round(stats.wins / stats.games * 100) : 0}%</dd></div>
            <div><dt>{t(locale, "hands")}</dt><dd>{stats.hands}</dd></div>
            <div><dt>{t(locale, "averageHands")}</dt><dd>{stats.games ? (stats.gameHands / stats.games).toFixed(1) : "0"}</dd></div>
            <div><dt>{t(locale, "callSuccess")}</dt><dd>{stats.calls ? Math.round(stats.callsMade / stats.calls * 100) : 0}%</dd></div>
            <div><dt>{t(locale, "euchresLabel")}</dt><dd>{stats.euchres}</dd></div>
            <div><dt>{t(locale, "aloneSuccess")}</dt><dd>{stats.aloneCalls ? Math.round(stats.aloneMade / stats.aloneCalls * 100) : 0}%</dd></div>
            <div><dt>{t(locale, "streak")}</dt><dd>{stats.streak}</dd></div>
          </dl>
          <div className="daily-record">
            <h3>{t(locale, "latestDaily")}</h3>
            {todayDaily ? (
              <p>
                <strong>{todayDaily.date}</strong> · {t(locale, todayDaily.won ? "dailyWon" : "dailyLost")} ·
                {" "}{t(locale, "marginLabel")} {todayDaily.margin > 0 ? "+" : ""}{todayDaily.margin} ·
                {" "}{t(locale, "hands")} {todayDaily.hands} · {t(locale, todayDaily.assisted ? "assisted" : "unassisted")}
                {todayDaily.callMatched !== undefined && (
                  <> · {t(locale, "dailyCallLabel")} {t(locale, todayDaily.callMatched ? "callMatchedShort" : "callMissedShort")}</>
                )}
              </p>
            ) : <p>{t(locale, "noDaily")}</p>}
          </div>
        </section>

        <section className="faq">
          <h2>{t(locale, "faqTitle")}</h2>
          {(["1", "2", "3"] as const).map((id) => (
            <details key={id}>
              <summary>{t(locale, `faq${id}` as "faq1")}</summary>
              <p>{t(locale, `faqa${id}` as "faqa1")}</p>
            </details>
          ))}
        </section>
      </main>

      {showEntry && (
        <section className="entry-layer" aria-labelledby="entry-title">
          <div className="entry-panel">
            <p className="entry-eyebrow">{t(locale, "entryEyebrow")}</p>
            <h1 id="entry-title">{t(locale, "entryTitle")}</h1>
            <p className="entry-lead">{t(locale, "entryLead")}</p>
            <div className="entry-options">
              <button className="entry-option entry-primary" autoFocus onClick={() => chooseEntry("tutorial")}>
                <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
                <strong>{t(locale, "entryTutorialTitle")}</strong>
                <span>{t(locale, "entryTutorialDesc")}</span>
              </button>
              <button className="entry-option" onClick={() => chooseEntry("quick")}>
                <i className="fa-solid fa-bolt" aria-hidden="true" />
                <strong>{t(locale, "entryQuickTitle")}</strong>
                <span>{t(locale, "entryQuickDesc")}</span>
              </button>
              <button className="entry-option" onClick={() => chooseEntry("daily")}>
                <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                <strong>{t(locale, "entryDailyTitle")}</strong>
                <span>{t(locale, "entryDailyDesc")}</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {!tutorialActive && (state.phase === "handResult" || state.phase === "completed") && state.lastHand && (
        <AccessibleDialog
          className="modal-dialog"
          labelledBy="result-title"
          onClose={() => {
            if (state.phase === "handResult") dispatch({ type: "NEXT_HAND" });
          }}
        >
          <section className="result-card">
            <p>{state.phase === "completed" ? t(locale, "matchComplete") : t(locale, "handComplete")}</p>
            <h2 id="result-title">{state.scores[0]} – {state.scores[1]}</h2>
            <p>{t(locale, state.lastHand.reason)}</p>
            <div className="trick-pips" aria-label={`${t(locale, "tricks")}: ${state.lastHand.tricks[0]} – ${state.lastHand.tricks[1]}`}>
              {"●".repeat(state.lastHand.tricks[0])}{"○".repeat(state.lastHand.tricks[1])}
            </div>
            {state.phase === "completed" ? (
              <>
                <strong>{t(locale, dailyScenario
                  ? (state.winner === 0 ? "dailyHandWon" : "dailyHandLost")
                  : (state.winner === 0 ? "teamWon" : "teamLost"))}</strong>
                <div className="result-metrics" aria-label={t(locale, "resultSummary")}>
                  <span>{t(locale, "hands")} <strong>{resultMetrics.hands}</strong></span>
                  <span>{t(locale, "callSuccess")} <strong>{resultCallRate}%</strong></span>
                  <span>{t(locale, "euchresLabel")} <strong>{resultMetrics.euchres}</strong></span>
                  <span><strong>{t(locale, state.assisted ? "assisted" : "unassisted")}</strong></span>
                </div>
                {dailyScenario && (
                  <div className="daily-review">
                    <h3>{t(locale, "dailyReviewTitle")}</h3>
                    <p>{fill(t(locale, "dailyFocus"), { kind: t(locale, dailyKindKey(dailyScenario.kind)) })}</p>
                    <p>{fill(t(locale, "dailyRecommended"), {
                      action: dailyActionText({ action: dailyScenario.recommended, suit: dailyScenario.facts.upcardSuit }, locale),
                    })}</p>
                    {dailyDecision && (
                      <p>{fill(t(locale, "dailyYourCall"), {
                        action: dailyActionText(dailyDecision, locale),
                        verdict: t(locale, dailyCallMatched(state, dailyScenario) ? "dailyMatched" : "dailyMissed"),
                      })}</p>
                    )}
                    <p>{fill(t(locale, "dailySimFacts"), {
                      order: String(dailyScenario.facts.orderEV),
                      pass: String(dailyScenario.facts.passEV),
                      alone: String(dailyScenario.facts.aloneEV),
                    })}</p>
                    <p className="daily-sim-note">{fill(t(locale, "dailySimNote"), { simulations: String(dailyScenario.simulations) })}</p>
                  </div>
                )}
                {!dailyScenario && swings.length > 0 && (
                  <div className="swing-list">
                    <h3>{t(locale, "swingTitle")}</h3>
                    <ul>
                      {swings.map((moment) => (
                        <li key={moment.handNumber}>{fill(t(locale, swingKindKey(moment.kind)), {
                          n: String(moment.handNumber),
                          points: String(moment.points),
                        })}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button className="primary" onClick={() => start(state.mode)}>{t(locale, "newMatch")}</button>
                {state.mode !== "daily" && <button onClick={() => start("daily")}>{t(locale, "playDaily")}</button>}
                <button onClick={shareResult}>{t(locale, "share")}</button>
              </>
            ) : (
              <button className="primary" onClick={() => dispatch({ type: "NEXT_HAND" })}>{t(locale, "nextHand")}</button>
            )}
          </section>
        </AccessibleDialog>
      )}

      {modal && (
        <AccessibleDialog
          className="modal-dialog"
          ariaLabel={t(locale, modal === "rules" ? "helpTitle" : modal === "log" ? "gameLog" : "settings")}
          onClose={() => setModal(null)}
        >
          <section className="modal">
            <button className="modal-close" onClick={() => setModal(null)} aria-label={t(locale, "close")}><i className="fa-solid fa-xmark" /></button>
            {modal === "rules" && <><h2>{t(locale, "helpTitle")}</h2><p>{t(locale, "helpText")}</p><p>{t(locale, "bowerBody")}</p><p>{t(locale, "scoreBody")}</p><p><a href={rulesPath}>{t(locale, "fullRules")} →</a></p></>}
            {modal === "log" && <><h2>{t(locale, "gameLog")}</h2><ol className="game-log">{state.history.slice(-20).map((item, index) => <li key={`${item}-${index}`}>{localizeLog(item, locale)}</li>)}</ol></>}
            {modal === "settings" && (
              <>
                <h2>{t(locale, "settings")}</h2>
                <label><input type="checkbox" checked={settings.sound} onChange={(event) => updateSetting("sound", event.target.checked)} /> {t(locale, "sound")}</label>
                <label><input type="checkbox" checked={settings.reducedMotion} onChange={(event) => updateSetting("reducedMotion", event.target.checked)} /> {t(locale, "reducedMotion")}</label>
                <label><input type="checkbox" checked={settings.confirmPlay} onChange={(event) => updateSetting("confirmPlay", event.target.checked)} /> {t(locale, "confirmPlay")}</label>
                <fieldset className="difficulty-field">
                  <legend>{t(locale, "difficulty")}</legend>
                  {(["relaxed", "standard", "sharp"] as Difficulty[]).map((level) => (
                    <label key={level}>
                      <input
                        type="radio"
                        name="difficulty"
                        checked={settings.difficulty === level}
                        onChange={() => updateSetting("difficulty", level)}
                      />
                      <span>
                        <strong>{t(locale, difficultyLabelKey(level))}</strong>
                        <small>{t(locale, difficultyDescKey(level))}</small>
                      </span>
                    </label>
                  ))}
                </fieldset>
                <div className="locale-switcher settings-locale" aria-label={t(locale, "language")}>
                  <a href="/" aria-current={locale === "en" ? "page" : undefined}>EN</a>
                  <a href="/zh/" aria-current={locale === "zh" ? "page" : undefined}>中文</a>
                  <a href="/ja/" aria-current={locale === "ja" ? "page" : undefined}>日本語</a>
                </div>
                <button onClick={() => setClearConfirmOpen(true)}>{t(locale, "clearData")}</button>
              </>
            )}
          </section>
        </AccessibleDialog>
      )}

      {pendingMode && (
        <AccessibleDialog className="modal-dialog" labelledBy="restart-title" onClose={() => setPendingMode(null)}>
          <section className="modal confirm-dialog">
            <h2 id="restart-title">{t(locale, "restartTitle")}</h2>
            <p>{t(locale, "restartConfirm")}</p>
            <div className="confirm-actions">
              <button data-autofocus onClick={() => setPendingMode(null)}>{t(locale, "cancel")}</button>
              <button className="primary" onClick={() => {
                const mode = pendingMode;
                setPendingMode(null);
                beginMatch(mode);
              }}>{t(locale, "startOver")}</button>
            </div>
          </section>
        </AccessibleDialog>
      )}

      {clearConfirmOpen && (
        <AccessibleDialog className="modal-dialog" labelledBy="clear-title" onClose={() => setClearConfirmOpen(false)}>
          <section className="modal confirm-dialog">
            <h2 id="clear-title">{t(locale, "clearData")}</h2>
            <p>{t(locale, "clearConfirm")}</p>
            <div className="confirm-actions">
              <button data-autofocus onClick={() => setClearConfirmOpen(false)}>{t(locale, "cancel")}</button>
              <button className="primary" onClick={() => {
                clearAllLocalData();
                setClearConfirmOpen(false);
                setSaved(null);
                setStats(loadStats());
                setState(newGame("quick", locale));
                setSettings({ ...DEFAULT_SETTINGS, locale });
                setModal(null);
              }}>{t(locale, "clearData")}</button>
            </div>
          </section>
        </AccessibleDialog>
      )}
    </div>
  );
}

interface TutorialPanelProps {
  step: number;
  state: GameState;
  humanCallEvent: string | undefined;
  locale: Locale;
  onNext: () => void;
  onExit: () => void;
  onStartMatch: () => void;
}

// The tutorial explains in place of the coach panel, reusing its grid area.
function TutorialPanel({ step, state, humanCallEvent, locale: language, onNext, onExit, onStartMatch }: TutorialPanelProps) {
  if (step === 6) {
    return (
      <aside className="tutorial-panel" aria-label={t(language, "tutorialTag")}>
        <h3><i className="fa-solid fa-graduation-cap" aria-hidden="true" /> {t(language, "tutorialTag")}</h3>
        <p>{t(language, "tutQuiet")}</p>
        <div className="tutorial-actions">
          <button onClick={onExit}>{t(language, "tutExit")}</button>
        </div>
      </aside>
    );
  }
  const titleKey = `tut${step}Title` as TranslationKey;
  const bodyKey = `tut${step}Body` as TranslationKey;
  let note: string | null = null;
  if (step === 2 && humanCallEvent?.startsWith("0:pass") && state.phase === "calling2") {
    note = t(language, "tut2Passed");
  } else if (step === 3 && humanCallEvent) {
    if (humanCallEvent.startsWith("0:order")) {
      note = fill(t(language, "tut3AfterOrder"), { suit: suitText[language][state.upcard.suit] });
    } else if (humanCallEvent.startsWith("0:call")) {
      const suit = humanCallEvent.split(":")[2] as Suit;
      note = fill(t(language, "tut3AfterCall"), { suit: suitText[language][suit] });
    } else {
      note = t(language, "tut3AfterPass");
    }
  } else if (step === 4 && state.tricks.length > 0) {
    note = fill(t(language, "tut4Winner"), { seat: seatText[language][state.tricks[0].winner] });
  }
  return (
    <aside className="tutorial-panel" aria-label={t(language, "tutorialTag")}>
      <h3>
        <i className="fa-solid fa-graduation-cap" aria-hidden="true" /> {t(language, "tutorialTag")}
        <span className="tutorial-progress">{fill(t(language, "tutProgress"), { n: String(step + 1) })}</span>
      </h3>
      <p><strong>{t(language, titleKey)}</strong></p>
      <p>{t(language, bodyKey)}</p>
      {note && <p className="tutorial-note">{note}</p>}
      <div className="tutorial-actions">
        {(step === 0 || step === 1 || step === 4) && (
          <button className="primary" onClick={onNext}>{t(language, "tutNext")}</button>
        )}
        {step === 5 && (
          <button className="primary" onClick={onStartMatch}>{t(language, "tutStartMatch")}</button>
        )}
        <button onClick={onExit}>{t(language, "tutExit")}</button>
      </div>
    </aside>
  );
}

function Scoreboard({ state }: { state: GameState }) {
  return (
    <div className="scoreboard" aria-label={t(locale, "scoreAria")}>
      <div><span>{t(locale, "we")}</span><strong>{state.scores[0]}</strong></div>
      <div><span>{t(locale, "target")}</span><strong>10</strong></div>
      <div><span>{t(locale, "they")}</span><strong>{state.scores[1]}</strong></div>
    </div>
  );
}

function SeatPanel({ seat, state, locale: language, className }: { seat: Seat; state: GameState; locale: Locale; className: string }) {
  const inactive = state.inactiveSeat === seat;
  return (
    <div className={`seat-panel ${className} ${state.currentSeat === seat ? "current" : ""} ${inactive ? "inactive" : ""}`}>
      {!inactive && <div className="card-fan">{Array.from({ length: Math.min(state.hands[seat].length, 5) }, (_, index) => <CardView key={index} hidden locale={language} compact />)}</div>}
      <div className="seat-label">
        <strong>{seatText[language][seat]}</strong>
        <span>{inactive ? t(language, "alone") : `${state.hands[seat].length} ${t(language, "cards")}`}</span>
        {state.dealer === seat && <b aria-label={t(language, "dealer")}>D</b>}
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return <div className="info-row"><i className={`fa-solid ${icon}`} aria-hidden="true" /><span><small>{label}</small><strong>{value}</strong></span></div>;
}

function phaseMessage(state: GameState, language: Locale): string {
  if (state.phase === "discard") return t(language, "discard");
  if (state.phase === "playing") return t(language, "yourTurn");
  if (state.phase === "trickResult") return t(language, "collecting");
  if (state.phase === "handResult") return t(language, "handComplete");
  if (state.phase === "completed") return t(language, "matchComplete");
  return "";
}

function phaseDetail(state: GameState, language: Locale): string {
  if (state.phase === "playing" && state.currentSeat !== HUMAN) return `${seatText[language][state.currentSeat]} ${t(language, "thinking")}`;
  if (state.phase === "discard" && state.currentSeat !== HUMAN) return `${seatText[language][state.currentSeat]} ${t(language, "thinking")}`;
  if ((state.phase === "calling1" || state.phase === "calling2") && state.currentSeat !== HUMAN) return `${seatText[language][state.currentSeat]} ${t(language, "thinking")}`;
  return phaseMessage(state, language);
}

type TranslationKey = Parameters<typeof t>[1];

function difficultyLabelKey(level: Difficulty): TranslationKey {
  return level === "relaxed" ? "difficultyRelaxed" : level === "sharp" ? "difficultySharp" : "difficultyStandard";
}

function difficultyDescKey(level: Difficulty): TranslationKey {
  return level === "relaxed" ? "difficultyRelaxedDesc" : level === "sharp" ? "difficultySharpDesc" : "difficultyStandardDesc";
}

function dailyKindKey(kind: "order" | "pass" | "alone"): TranslationKey {
  return kind === "order" ? "kindOrder" : kind === "alone" ? "kindAlone" : "kindPass";
}

function swingKindKey(kind: SwingKind): TranslationKey {
  const keys = {
    "own-euchred": "swingOwnEuchred",
    "opp-euchred": "swingOppEuchred",
    "own-march": "swingOwnMarch",
    "opp-march": "swingOppMarch",
    "own-lone-march": "swingOwnLoneMarch",
    "opp-lone-march": "swingOppLoneMarch",
    "own-lone-fail": "swingOwnLoneFail",
    "opp-lone-fail": "swingOppLoneFail",
  } as const;
  return keys[kind];
}

function dailyActionText(decision: DailyCallDecision, language: Locale): string {
  if (decision.action === "pass") return t(language, "actPass");
  const suit = decision.suit ? suitText[language][decision.suit] : "";
  if (decision.action === "order") return fill(t(language, "actOrder"), { suit });
  if (decision.action === "orderAlone") return fill(t(language, "actOrderAlone"), { suit });
  if (decision.action === "call") return fill(t(language, "actCall"), { suit });
  return fill(t(language, "actCallAlone"), { suit });
}

interface TrumpFacts {
  count: number;
  hasRight: boolean;
  hasLeft: boolean;
}

// Fact sheet for the calling UI: effective trump length (Left Bower counts)
// and bower possession, with no recommendation attached.
function trumpFacts(hand: GameState["hands"][number], suit: Suit): TrumpFacts {
  return {
    count: hand.filter((card) => effectiveSuit(card, suit) === suit).length,
    hasRight: hand.some((card) => isRightBower(card, suit)),
    hasLeft: hand.some((card) => isLeftBower(card, suit)),
  };
}

function localizeCoach(advice: CoachAdvice, language: Locale): string {
  if (advice.kind === "order") return fill(t(language, "coachOrder"), { suit: suitText[language][advice.suit] });
  if (advice.kind === "pass") return t(language, "coachPass");
  if (advice.kind === "call") return fill(t(language, "coachCall"), { suit: suitText[language][advice.suit] });
  if (advice.kind === "wait") return t(language, "coachWait");
  if (advice.kind === "follow") {
    return fill(t(language, "coachFollow"), {
      rank: advice.rank,
      suit: suitText[language][advice.suit],
    });
  }
  if (advice.kind === "balance") return fill(t(language, "coachBalance"), { rank: advice.rank });
  return t(language, "coachObserve");
}

function localizeLog(event: string, language: Locale): string {
  if (event.startsWith("locale:")) return t(language, "logStarted");
  if (event === "round:2") return t(language, "logRound2");
  const hand = event.match(/^hand:(made|march|euchred|lone-made|lone-march):(-?\d+)-(-?\d+)$/);
  if (hand) {
    return `${t(language, "handComplete")}: ${t(language, hand[1] as "made")} (${hand[2]}–${hand[3]})`;
  }
  const action = event.match(/^([0-3]):(pass|order|call|discard|play)(?::([^:]+))?(?::(alone))?$/);
  if (!action) return t(language, "actionUnavailable");
  const seat = seatText[language][Number(action[1])];
  if (action[2] === "pass") return fill(t(language, "logPassed"), { seat });
  if (action[2] === "discard") return fill(t(language, "logDiscarded"), { seat });
  if (action[2] === "order" || action[2] === "call") {
    const suit = action[3] as Suit;
    return fill(t(language, action[2] === "order" ? "logOrdered" : "logCalled"), {
      seat,
      suit: suitText[language][suit],
      alone: action[4] ? t(language, "logAlone") : "",
    });
  }
  const [suit, rank] = (action[3] ?? "").split("-");
  const card = suit in suitText[language]
    ? `${rank} ${suitText[language][suit as Suit]}`
    : action[3] ?? "";
  return fill(t(language, "logPlayed"), { seat, card });
}
